import React, { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import dayjs, { Dayjs } from "dayjs";
import CalculatorTextField from "./CalculatorTextField";

import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  Engineering as EngineeringIcon,
  PersonOff as PersonOffIcon,
  CalendarMonth as CalendarMonthIcon,
  AccessTime as AccessTimeIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  HelpOutline as HelpOutlineIcon,
  ArrowRightAlt as ArrowRightAltIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  LocalHospital as LocalHospitalIcon,
  Checklist as ChecklistIcon,
  OpenInNew as OpenInNewIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";

import { DamageType, imageUrl, type BobEvent, type BobEventSetter, type EditedTestData, type TestData } from "./helpers";
import TimePicker from "react-time-picker";
import SingleDatePicker from "@/shared/forms/SingleDatePicker";
import { ImageUploadPicker } from "./ImageUploadPicker";
import { ChevronsDown, ChevronsUp, CircleDot } from "lucide-react";

type EventVisTypes =
  | "hidden"
  | "full"
  | "title"
  | "dueDate"
  | "copyRule"
  | "assignee"
  | "damages"
  | "reopen"
  | "scheduledDate"
  | "priority"
  | "meterReading"
  | "testing"
  | "delete"
  | "complete"
  | "note";

export interface EventRef {
  openModal: (initialState: BobEvent | null, visibility: EventVisTypes) => void;
}

const copyAsDamage = (state: BobEventSetter, resource: number, dateOnlyScheduling = false): BobEventSetter => {
  const overwrite: Partial<BobEventSetter> = {
    id: 0,
    scheduledAt: null,
    ...(dateOnlyScheduling ? { scheduledDate: null } : {}),
    completedAt: null,
    damageRegulationData: null,
    resource,
    dueAt: null,
    dueAtAmount: null,
    copyRuleDurationUnit: null,
    copyRuleDurationValue: null,
    category: "damage",
    maintenanceType: null,
    title: "Mängelbericht aus Wartung",
    priority: "normal",
    assignedTo: "service",
  };
  return { ...state, ...overwrite };
};

const prepareState = (state: BobEvent | null, resource: number, visibility: EventVisTypes, testingContent: TestData[], dateOnlyScheduling = false): BobEventSetter => {
  const defaults: BobEventSetter = {
    id: 0,
    scheduledAt: null,
    ...(dateOnlyScheduling ? { scheduledDate: null } : {}),
    completedAt: null,
    dueAt: null,
    damageRegulationData: null,
    dueAtAmount: null,
    copyRuleDurationUnit: null,
    copyRuleDurationValue: null,
    category: "damage",
    maintenanceType: null,
    title: "",
    priority: "normal",
    assignedTo: "service",
    messages: [
      {
        id: 0,
        attachments: [],
        runningHours: {
          id: 0,
          resource,
          runningHours: 0,
        },
        format: visibility === "testing" ? "testing_json" : "text",
        message: visibility === "testing" ? testingContent.map((x) => ({ ...x, failed: null, comment: "" })) : "",
      },
    ],
  };

  if (!state) return { ...defaults, resource };

  const mapped: Partial<BobEventSetter> = {
    id: state.id,
    scheduledAt: state.scheduledAt,
    ...(dateOnlyScheduling ? { scheduledDate: state.scheduledDate ?? null } : {}),
    deleted: visibility === "delete",
    completedAt:
      visibility === "complete" ? state?.completedAt || dayjs().utc().format("YYYY-MM-DD") : visibility === "reopen" ? null : state.completedAt,
    dueAt: state.dueAt,
    damageRegulationData: state.damageRegulationData,
    dueAtAmount: state.dueAtAmount,
    copyRuleDurationUnit: state.copyRuleDurationUnit,
    copyRuleDurationValue: state.copyRuleDurationValue,
    category: state.category,
    maintenanceType: state.maintenanceType,
    title: state.title,
    priority: state.priority,
    assignedTo: state.assignedTo,
  };

  return { ...defaults, ...mapped };
};

const fix = (state: BobEventSetter, modalVisibility: EventVisTypes): BobEventSetter => {
  if (modalVisibility === "meterReading" || modalVisibility === "testing" || modalVisibility === "complete") {
    state.completedAt = state.completedAt || dayjs().utc().format("YYYY-MM-DD HH:mm:ss");
    if (state.completedAt.length === 10) state.completedAt += " " + dayjs().utc().format("HH:mm:ss");
  }

  if (modalVisibility === "delete") {
    state.deleted = true;
  }

  if (modalVisibility === "complete") {
    state.messages[0].message = "Erledigt. " + state.messages[0].message;
  }

  if (modalVisibility === "reopen") {
    state.completedAt = null;

    if (typeof state.messages[0].message === "string") {
      state.messages[0].message = "Erneut geöffnet. " + state.messages[0].message;
    }
  }

  if (state.messages[0].runningHours?.runningHours === 0) state.messages[0].runningHours = null;
  //if (!state.messages[0].runningHours && state.messages[0].message.length === 0 && state.messages[0].attachments.length === 0) state.messages = [];

  return state;
};

const maintenanceTypes = [
  ["uvv", "UVV"],
  ["tuev", "TÜV"],
  ["inspection", "Inspektion"],
  ["sp", "SP"],
  ["tacho", "Tacho"],
  ["gas", "Gas"],
  ["other", "Sonstige"],
] as const;

export const NumberInput = ({
  value,
  onChange,
  fallback,
  placeholder,
}: {
  value: string | number;
  fallback: number;
  placeholder?: string | undefined;
  onChange: (n: number) => void;
}) => {
  const [sval, setSval] = useState(String(value ?? ""));

  useEffect(() => setSval(String(value ?? "")), [value]);

  return (
    <TextField
      size="small"
      fullWidth
      placeholder={placeholder}
      value={sval}
      inputMode="numeric"
      onChange={(e) => setSval(e.target.value)}
      onBlur={() => onChange(Number.isNaN(parseInt(sval, 10)) ? fallback : parseInt(sval, 10))}
      sx={{ mb: 2 }}
    />
  );
};

function titleFor(modalVisibility: EventVisTypes, state: BobEventSetter, role: "site" | "service") {
  if (modalVisibility === "assignee") return "Zuständigkeit ändern";
  if (modalVisibility === "copyRule") return "Wiederholung anpassen";
  if (modalVisibility === "damages") return "Regulierungsdaten erfassen";
  if (modalVisibility === "dueDate") return "Fälligkeit ändern";
  if (modalVisibility === "note") return "Kommentar hinzufügen";
  if (modalVisibility === "reopen") return "Erneut öffnen";
  if (modalVisibility === "complete") return "Als Erledigt markieren";
  if (modalVisibility === "delete") return "Löschen";
  if (modalVisibility === "meterReading") return "Betriebsstunden erfassen";
  if (modalVisibility === "testing") return "Wartung";
  if (modalVisibility === "scheduledDate") return "Termin planen";
  if (modalVisibility === "priority") return "Priorität ändern";
  if (modalVisibility === "title") return "Titel bearbeiten";
  if (state.id > 0) return "Ereignis bearbeiten";
  return role === "service" ? "Neues Ereignis" : "Mängelbericht";
}

function categoryIcon(category: BobEventSetter["category"]) {
  switch (category) {
    case "damage":
      return WarningIcon;
    case "testing":
      return ChecklistIcon;
    case "maintenance":
      return LocalHospitalIcon;
    case "external":
      return OpenInNewIcon;
    case "meterReading":
      return TimerIcon;
    default:
      return WarningIcon;
  }
}

const EventEditModal = forwardRef<
  EventRef,
  {
    deviceId: number;
    deviceNumber: string;
    onCreate: (id: number) => void;
    lastReading: number | null;
    testingContent?: TestData[];
    noHours: boolean;
    ApiService: any;
    isFPM: boolean;
    dateOnlyScheduling?: boolean;
    role: "site" | "service";
  }
>(({ deviceId, deviceNumber, lastReading, noHours, testingContent, onCreate, ApiService, role, isFPM = false, dateOnlyScheduling = false }, ref) => {
  const [modalVisibility, setModalVisibility] = useState<EventVisTypes>("hidden");
  const [state, setState] = useState<BobEventSetter | null>(null);
  const [allowTestingCategory, setAllowTestingCategory] = useState(false);
  const [timeEnabled, setTimeEnabled] = useState(false);

  const openModal = useCallback<EventRef["openModal"]>(
    (initialState, visibility: EventVisTypes = "full") => {
      setAllowTestingCategory(initialState?.category === "testing");
      console.log("OPEN MODAL");
      setState(prepareState(initialState ?? null, deviceId, visibility, testingContent ?? [], dateOnlyScheduling));
      setTimeEnabled(!!initialState?.scheduledAt);
      setModalVisibility(visibility);
    },
    [deviceId, testingContent, dateOnlyScheduling],
  );

  useImperativeHandle(ref, () => ({ openModal }), [openModal]);

  const queryClient = useQueryClient();

  const mutation = useMutation<number, AxiosResponse, { event: BobEventSetter; createDamageReport: boolean }>({
    mutationFn: async ({ event, createDamageReport }) => {
      let changeId = 0;
      if (event.id > 0) {
        await ApiService.editEntity("bobEvent", event.id, "resource")(event);
        if (createDamageReport) {
          const damageResponse = await ApiService.createEntity("bobEvent", true, { v: "resource" })(copyAsDamage(event, deviceId, dateOnlyScheduling));
          changeId = damageResponse.id;
        }
      } else {
        const resp = await ApiService.createEntity("bobEvent", true, { v: "resource" })(event);
        changeId = resp.id;
      }
      return changeId;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["getDeviceEvents", "" + deviceNumber] });
      queryClient.invalidateQueries({ queryKey: ["getBobEventMessages", "" + (state?.id || 0)] });
      queryClient.invalidateQueries({ queryKey: ["getBobEvent", state?.id || 0] });
      queryClient.invalidateQueries({ queryKey: ["getEvents"] });
      queryClient.invalidateQueries({ queryKey: ["getServiceByUser"] });
      if (response > 0) onCreate(response);
      setModalVisibility("hidden");
    },
  });

  const maintenanceWarning =
    state?.category === "maintenance" &&
    (!state.maintenanceType || (!state.dueAt && !state.dueAtAmount) || !state.copyRuleDurationUnit || !state.copyRuleDurationValue);
  // Completion and the other focused dialogs do not expose the maintenance
  // type. Keep them usable for legacy events whose type has not been set.
  const showScheduled = modalVisibility === "scheduledDate" || (modalVisibility === "full" && state?.assignedTo === role);
  const missingScheduledTime = showScheduled && dateOnlyScheduling && timeEnabled && !state?.scheduledAt;
  const valid = !missingScheduledTime && (modalVisibility !== "full" || state?.category !== "maintenance" || !!state.maintenanceType);

  if (!state) return null;

  const open = modalVisibility !== "hidden";

  const showCategory = modalVisibility === "full" && role === "service";
  const showTitle = modalVisibility === "full" || modalVisibility === "title";
  const showAssignee = modalVisibility === "assignee";
  const showDamageRegulation = modalVisibility === "damages" || (modalVisibility === "full" && isFPM && state.category === "damage");
  const showPriority = modalVisibility === "priority" || (modalVisibility === "full" && state.assignedTo === role);
  const showDue = modalVisibility === "dueDate" || (modalVisibility === "full" && state.assignedTo === role);
  const showCopyRule =
    modalVisibility === "copyRule" ||
    (modalVisibility === "full" && state.assignedTo === role && (!!state.copyRuleDurationUnit || state.category !== "damage"));
  const showTestingBlocks = modalVisibility === "testing" && typeof state.messages[0]?.message !== "string";
  const showHours = !noHours;
  const showMessageAndAttachments =
    modalVisibility === "full" ||
    modalVisibility === "note" ||
    modalVisibility === "testing" ||
    modalVisibility === "meterReading" ||
    modalVisibility === "complete" ||
    modalVisibility === "delete" ||
    modalVisibility === "reopen";

  return (
    <Dialog
      open={open}
      onClose={() => setModalVisibility("hidden")}
      fullWidth
      maxWidth="md"
      scroll={modalVisibility === "full" || modalVisibility === "testing" ? "paper" : "body"}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {titleFor(modalVisibility, state, role)}
          </Typography>
        </Box>
        <IconButton onClick={() => setModalVisibility("hidden")} size="small" aria-label="Schließen">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {showCategory ? (
            <Fragment>
              <Typography variant="subtitle2">Ereignisart</Typography>
              <ToggleButtonGroup
                value={state.category}
                exclusive
                onChange={(_, category: BobEventSetter["category"] | null) => {
                  if (!category) return;
                  setState((s) =>
                    !s
                      ? s
                      : {
                          ...s,
                          category,
                          assignedTo: category === "testing" ? "site" : "service",
                          maintenanceType: category === "maintenance" ? s.maintenanceType : null,
                          title: {
                            external: "Externer Termin",
                            meterReading: "Zählerstand ablesen",
                            testing: "Regelmäßige Wartung",
                            maintenance: "Prüfung/Inspektion",
                            damage: "",
                          }[category],
                        },
                  );
                }}
                sx={{ flexWrap: "wrap" }}
              >
                {(
                  [
                    { value: "damage", label: isFPM ? "Schaden" : "Mängelbericht" },
                    ...(allowTestingCategory ? [{ value: "testing" as const, label: "Regelmäßige Wartung" }] : []),
                    { value: "maintenance", label: "Prüfung/Inspektion" },
                  ] as Array<{ value: BobEventSetter["category"]; label: string }>
                ).map((opt) => {
                  const Icon = categoryIcon(opt.value);
                  return (
                    <ToggleButton key={opt.value} value={opt.value} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Icon fontSize="small" />
                      <span>{opt.label}</span>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
              {state.category === "maintenance" ? (
                <Fragment>
                  <Typography variant="subtitle2">Art der Prüfung</Typography>
                  <ToggleButtonGroup
                    value={state.maintenanceType}
                    exclusive
                    onChange={(_, maintenanceType: NonNullable<BobEventSetter["maintenanceType"]> | null) => {
                      if (!maintenanceType) return;
                      setState((s) =>
                        !s
                          ? s
                          : {
                              ...s,
                              maintenanceType,
                              title: maintenanceTypes.find(([value]) => value === maintenanceType)?.[1] ?? s.title,
                              ...(s.id > 0
                                ? {}
                                : maintenanceType === "inspection"
                                  ? {
                                      dueAt: null,
                                      dueAtAmount: (Math.floor((lastReading ?? 0) / 500) + 1) * 500,
                                      copyRuleDurationUnit: "amount" as const,
                                      copyRuleDurationValue: 500,
                                    }
                                  : {
                                      copyRuleDurationUnit: "year" as const,
                                      copyRuleDurationValue: 1,
                                      dueAt: dayjs().add(1, "year").format("YYYY-MM-DD"),
                                      dueAtAmount: null,
                                    }),
                            },
                      );
                    }}
                    sx={{ flexWrap: "wrap" }}
                  >
                    {maintenanceTypes.map(([value, label]) => (
                      <ToggleButton key={value} value={value}>
                        {label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Fragment>
              ) : null}
            </Fragment>
          ) : null}

          {maintenanceWarning ? <Typography color="error">Prüfungen benötigen eine Fälligkeit und eine Wiederholung.</Typography> : null}

          {showTitle ? (
            <Fragment>
              <Typography variant="subtitle2">Titel</Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Bitte Titel eingeben"
                value={state.title}
                onChange={(e) => setState((s) => (!s ? s : { ...s, title: e.target.value }))}
              />
            </Fragment>
          ) : null}

          {showAssignee ? (
            <Fragment>
              <Typography variant="subtitle2">Zuständigkeit</Typography>
              <ToggleButtonGroup
                value={state.assignedTo}
                exclusive
                onChange={(_, assignedTo: BobEventSetter["assignedTo"] | null) => {
                  if (assignedTo === null) return;
                  setState((s) => (!s ? s : { ...s, assignedTo }));
                }}
              >
                <ToggleButton value="site" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <EngineeringIcon fontSize="small" />
                  Baustelle
                </ToggleButton>
                <ToggleButton value="service" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <BuildIcon fontSize="small" />
                  Werkstatt
                </ToggleButton>
                <ToggleButton value="none" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <PersonOffIcon fontSize="small" />
                  Niemand
                </ToggleButton>
              </ToggleButtonGroup>
            </Fragment>
          ) : null}

          {showPriority ? (
            <Fragment>
              <Typography variant="subtitle2">Priorität</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <ToggleButtonGroup
                  value={state.priority}
                  exclusive
                  onChange={(_, priority: BobEventSetter["priority"] | null) => {
                    if (priority === null) return;
                    setState((s) => (!s ? s : { ...s, priority }));
                  }}
                >
                  <ToggleButton value="low" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <ChevronsDown fontSize="small" />
                    Niedrig
                  </ToggleButton>
                  <ToggleButton value="normal" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CircleDot fontSize="small" />
                    Normal
                  </ToggleButton>
                  <ToggleButton value="high" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <ChevronsUp fontSize="small" />
                    Hoch
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Fragment>
          ) : null}

          {showDue ? (
            <Fragment>
              <Typography variant="subtitle2">Fälligkeit</Typography>

              <ToggleButtonGroup
                value={!!state.dueAtAmount ? "AMOUNT" : !!state.dueAt ? "DATE" : "NONE"}
                exclusive
                onChange={(_, dueType: "AMOUNT" | "DATE" | "NONE" | null) => {
                  if (!dueType) return;
                  setState((s) =>
                    !s
                      ? s
                      : {
                          ...s,
                          dueAt: dueType === "DATE" ? dayjs().add(1, "week").format("YYYY-MM-DD") : null,
                          dueAtAmount: dueType === "AMOUNT" ? 1000 : null,
                        },
                  );
                }}
              >
                <ToggleButton value="NONE" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CloseIcon fontSize="small" />
                  Nicht festgelegt
                </ToggleButton>
                <ToggleButton value="DATE" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CalendarMonthIcon fontSize="small" />
                  Datum
                </ToggleButton>
                {state.category !== "damage" || !!state.dueAtAmount ? (
                  <ToggleButton value="AMOUNT" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <AccessTimeIcon fontSize="small" />
                    Betriebsstunden
                  </ToggleButton>
                ) : null}
              </ToggleButtonGroup>

              {!!state.dueAt ? (
                <Fragment>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Fällig am
                  </Typography>
                  <SingleDatePicker
                    value={!!state.dueAt ? dayjs.utc(state.dueAt).local() : null}
                    onChange={(dueAt: Dayjs | null) => setState((s) => (!s ? s : { ...s, dueAt: !dueAt ? null : dueAt.format("YYYY-MM-DD") }))}
                  />
                </Fragment>
              ) : null}

              {!!state.dueAtAmount ? (
                <Fragment>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Fällig nach Betriebsstunde
                  </Typography>
                  <NumberInput
                    fallback={1000}
                    value={state.dueAtAmount}
                    onChange={(dueAtAmount) => setState((s) => (!s ? s : { ...s, dueAtAmount }))}
                  />
                  {lastReading !== null && state.dueAtAmount < lastReading ? (
                    <Typography variant="body2" sx={{ color: "error.main" }}>
                      Der eingegebene Wert ist niedriger als der aktuelle Wert ({lastReading} h)!
                    </Typography>
                  ) : null}
                </Fragment>
              ) : null}
            </Fragment>
          ) : null}

          {showScheduled ? (
            <Fragment>
              <Typography variant="subtitle2">Geplanter Termin</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <SingleDatePicker
                  value={dateOnlyScheduling && state.scheduledDate ? dayjs(state.scheduledDate) : !!state.scheduledAt ? dayjs.utc(state.scheduledAt).local() : null}
                  onChange={(datePart: Dayjs | null) => {
                    if (dateOnlyScheduling && !datePart) setTimeEnabled(false);
                    setState((s) => {
                      if (!s) return s;

                      if (dateOnlyScheduling) {
                        if (datePart && timeEnabled && s.scheduledAt) {
                          const time = dayjs.utc(s.scheduledAt).local().format("HH:mm:ss");
                          return { ...s, scheduledDate: null,
                            scheduledAt: dayjs(`${datePart.format("YYYY-MM-DD")} ${time}`).utc().format("YYYY-MM-DD HH:mm:ss") };
                        }
                        return { ...s, scheduledDate: datePart?.format("YYYY-MM-DD") ?? null, scheduledAt: null };
                      }

                      if (!datePart) {
                        return {
                          ...s,
                          scheduledAt: null,
                        };
                      }

                      const timePart = s.scheduledAt ? dayjs.utc(s.scheduledAt).local().format("HH:mm:ss") : "00:00:00";

                      const localDateTime = dayjs(`${datePart.format("YYYY-MM-DD")} ${timePart}`, "YYYY-MM-DD HH:mm:ss");

                      return {
                        ...s,
                        scheduledAt: localDateTime.utc().format("YYYY-MM-DD HH:mm:ss"),
                      };
                    });
                  }}
                />
                {dateOnlyScheduling ? <FormControlLabel label="Uhrzeit festlegen" control={
                  <Checkbox checked={timeEnabled} disabled={!state.scheduledDate && !state.scheduledAt}
                    onChange={(_, enabled) => {
                      setTimeEnabled(enabled);
                      if (!enabled) setState((s) => !s ? s : { ...s,
                        scheduledDate: s.scheduledAt ? dayjs.utc(s.scheduledAt).local().format("YYYY-MM-DD") : s.scheduledDate ?? null,
                        scheduledAt: null });
                    }} />
                } /> : null}
                {(dateOnlyScheduling ? timeEnabled : !!state.scheduledAt) ? (
                  <TimePicker
                    clearIcon={null}
                    onChange={(hourPart: string) => {
                      setState((s) => {
                        if (!s) return s;
                        if (dateOnlyScheduling) {
                          const date = s.scheduledDate ?? (s.scheduledAt ? dayjs.utc(s.scheduledAt).local().format("YYYY-MM-DD") : null);
                          if (!date) return s;
                          if (!hourPart) return { ...s, scheduledDate: date, scheduledAt: null };
                          const localTime = dayjs(`${date} ${hourPart}`, "YYYY-MM-DD HH:mm");
                          if (!localTime.isValid()) return s;
                          return { ...s, scheduledDate: null, scheduledAt: localTime.utc().format("YYYY-MM-DD HH:mm:ss") };
                        }
                        return {
                              ...s,
                              scheduledAt: dayjs(
                                (!!s.scheduledAt ? dayjs(s.scheduledAt).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")) + " " + hourPart,
                              )
                                .utc()
                                .format("YYYY-MM-DD HH:mm:ss"),
                            };
                      });
                    }}
                    disableClock
                    value={state.scheduledAt ? dayjs.utc(state.scheduledAt).local().format("HH:mm") : null}
                  />
                ) : null}
                {!!state.scheduledAt || (dateOnlyScheduling && !!state.scheduledDate) ? (
                  <Tooltip title="Termin entfernen">
                    <IconButton size="small" aria-label="Termin entfernen" onClick={() => {
                      setTimeEnabled(false);
                      setState((s) => (!s ? s : { ...s, scheduledAt: null, ...(dateOnlyScheduling ? { scheduledDate: null } : {}) }));
                    }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
              {missingScheduledTime ? <Typography color="error" role="alert">Bitte Uhrzeit wählen.</Typography> : null}
            </Fragment>
          ) : null}

          {modalVisibility === "complete" ? (
            <Fragment>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Erledigt am
              </Typography>
              <SingleDatePicker
                value={!!state.completedAt ? dayjs.utc(state.completedAt).local() : null}
                onChange={(completedAt: Dayjs | null) =>
                  setState((s) => (!s ? s : { ...s, completedAt: !completedAt ? null : completedAt.format("YYYY-MM-DD") }))
                }
              />
            </Fragment>
          ) : null}

          {showCopyRule ? (
            <Fragment>
              <Typography variant="subtitle2">Wiederholung</Typography>

              <ToggleButtonGroup
                value={!state.copyRuleDurationUnit ? "NONE" : state.copyRuleDurationUnit === "amount" ? "AMOUNT" : "TIME"}
                exclusive
                onChange={(_, copyType: "NONE" | "AMOUNT" | "TIME" | null) => {
                  if (!copyType) return;
                  setState((s) =>
                    !s
                      ? s
                      : {
                          ...s,
                          copyRuleDurationUnit: copyType === "NONE" ? null : copyType === "AMOUNT" ? "amount" : "week",
                          copyRuleDurationValue: copyType === "NONE" ? null : copyType === "AMOUNT" ? 500 : 1,
                        },
                  );
                }}
              >
                <ToggleButton value="NONE" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CloseIcon fontSize="small" />
                  Keine Wiederholung
                </ToggleButton>
                <ToggleButton value="TIME" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CalendarMonthIcon fontSize="small" />
                  Nach Zeit
                </ToggleButton>
                <ToggleButton value="AMOUNT" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <AccessTimeIcon fontSize="small" />
                  Nach Betriebsstunden
                </ToggleButton>
              </ToggleButtonGroup>

              {state.copyRuleDurationUnit === "amount" ? (
                <Fragment>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Betriebsstunden
                  </Typography>
                  <NumberInput
                    fallback={500}
                    value={state.copyRuleDurationValue || 500}
                    onChange={(copyRuleDurationValue) => setState((s) => (!s ? s : { ...s, copyRuleDurationValue }))}
                  />
                </Fragment>
              ) : !!state.copyRuleDurationUnit && !!state.copyRuleDurationValue ? (
                <Fragment>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Intervall
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <IconButton
                      size="small"
                      disabled={state.copyRuleDurationValue <= 1}
                      onClick={() => setState((s) => (!s ? s : { ...s, copyRuleDurationValue: (s.copyRuleDurationValue ?? 2) - 1 }))}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>

                    <Typography sx={{ minWidth: 40, textAlign: "center" }}>{state.copyRuleDurationValue}</Typography>

                    <IconButton
                      size="small"
                      onClick={() => setState((s) => (!s ? s : { ...s, copyRuleDurationValue: (s.copyRuleDurationValue ?? 0) + 1 }))}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>

                    <ToggleButtonGroup
                      value={state.copyRuleDurationUnit}
                      exclusive
                      onChange={(_, unit: "day" | "week" | "month" | "year" | null) => {
                        if (!unit) return;
                        setState((s) => (!s ? s : { ...s, copyRuleDurationUnit: unit }));
                      }}
                      size="small"
                    >
                      <ToggleButton value="day">Tag{state.copyRuleDurationValue > 1 ? "e" : ""}</ToggleButton>
                      <ToggleButton value="week">Woche{state.copyRuleDurationValue > 1 ? "n" : ""}</ToggleButton>
                      <ToggleButton value="month">Monat{state.copyRuleDurationValue > 1 ? "e" : ""}</ToggleButton>
                      <ToggleButton value="year">Jahr{state.copyRuleDurationValue > 1 ? "e" : ""}</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </Fragment>
              ) : null}
            </Fragment>
          ) : null}

          {showTestingBlocks ? (
            <Fragment>
              {(state.messages[0].message as EditedTestData[]).map((block, i) => (
                <ChecklistBlockItem key={i} block={block} index={i} state={state} setState={setState} />
              ))}
            </Fragment>
          ) : null}

          {showDamageRegulation ? (
            <Fragment>
              {modalVisibility === "full" && <Divider />}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Schadensart
              </Typography>
              <ToggleButtonGroup
                value={!!state.damageRegulationData?.damageType ? state.damageRegulationData.damageType : "technical_defect"}
                exclusive
                onChange={(_, damageType: DamageType) => {
                  if (!damageType) return;
                  setState((s) =>
                    !s
                      ? s
                      : {
                          ...s,
                          damageRegulationData: {
                            ...(s.damageRegulationData ?? {}),
                            damageType: !damageType ? undefined : damageType,
                          },
                        },
                  );
                }}
                size="small"
              >
                <ToggleButton value="technical_defect">Technischer Defekt</ToggleButton>
                <ToggleButton value="casco_own_fault">Kasko</ToggleButton>
                <ToggleButton value="liability_third_party_fault">Haftpflicht (Fremdverschulden)</ToggleButton>
                <ToggleButton value="liability_own_fault">Haftpflicht (Eigenverschulden)</ToggleButton>
                <ToggleButton value="partial_casco">Teilkasko</ToggleButton>
              </ToggleButtonGroup>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    {(!!state.damageRegulationData?.damageType ? state.damageRegulationData.damageType : "technical_defect") === "technical_defect"
                      ? "Schadensdatum"
                      : "Unfalldatum"}
                  </Typography>
                  <SingleDatePicker
                    value={!!state.damageRegulationData?.incidentDate ? dayjs(state.damageRegulationData?.incidentDate) : null}
                    small
                    onChange={(incidentDate: Dayjs | null) =>
                      setState((s) =>
                        !s
                          ? s
                          : {
                              ...s,
                              damageRegulationData: {
                                ...(s.damageRegulationData ?? {}),
                                incidentDate: !incidentDate ? undefined : incidentDate.format("YYYY-MM-DD"),
                              },
                            },
                      )
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Kosten laut Rechnung
                  </Typography>
                  <CalculatorTextField
                    value={state.damageRegulationData?.invoiceCost ?? 0}
                    setValue={(invoiceCost: number) => {
                      setState((s) =>
                        !s
                          ? s
                          : {
                              ...s,
                              damageRegulationData: {
                                ...(s.damageRegulationData ?? {}),
                                invoiceCost,
                              },
                            },
                      );
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Selbstbeteiligung
                  </Typography>
                  <CalculatorTextField
                    value={state.damageRegulationData?.deductible ?? 0}
                    setValue={(deductible: number) => {
                      setState((s) =>
                        !s
                          ? s
                          : {
                              ...s,
                              damageRegulationData: {
                                ...(s.damageRegulationData ?? {}),
                                deductible,
                              },
                            },
                      );
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Erstattungsfähiger Betrag
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    disabled
                    value={Math.max((state.damageRegulationData?.invoiceCost ?? 0) - (state.damageRegulationData?.deductible ?? 0), 0)}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Erhaltene Erstattung
                  </Typography>
                  <CalculatorTextField
                    value={state.damageRegulationData?.reimbursedAmount ?? 0}
                    setValue={(reimbursedAmount: number) => {
                      setState((s) =>
                        !s
                          ? s
                          : {
                              ...s,
                              damageRegulationData: {
                                ...(s.damageRegulationData ?? {}),
                                reimbursedAmount,
                              },
                            },
                      );
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Offene Differenz
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    disabled
                    value={
                      Math.max((state.damageRegulationData?.invoiceCost ?? 0) - (state.damageRegulationData?.deductible ?? 0), 0) -
                      (state.damageRegulationData?.reimbursedAmount ?? 0)
                    }
                  />
                </Grid>
              </Grid>
            </Fragment>
          ) : null}

          {showMessageAndAttachments ? (
            <Fragment>
              <Divider />
              {showHours ? (
                <Fragment>
                  <Typography variant="subtitle2">Aktuelle Betriebsstunden</Typography>
                  <NumberInput
                    fallback={0}
                    placeholder="Wenn möglich bitte erfassen"
                    value={state.messages[0]?.runningHours?.runningHours || ""}
                    onChange={(runningHours) => {
                      setState((s) =>
                        !s || !s.messages[0].runningHours
                          ? s
                          : {
                              ...s,
                              messages: [
                                {
                                  ...s.messages[0],
                                  runningHours: { ...s.messages[0].runningHours, runningHours },
                                },
                              ],
                            },
                      );
                    }}
                  />

                  {!!lastReading && !!state.messages[0]?.runningHours?.runningHours && state.messages[0]?.runningHours?.runningHours < lastReading ? (
                    <Typography variant="body2" sx={{ color: "error.main" }}>
                      Der eingegebene Wert ist niedriger als der aktuelle Wert ({lastReading} h)!
                    </Typography>
                  ) : null}
                </Fragment>
              ) : null}

              {!state.messages[0] || typeof state.messages[0].message !== "string" ? null : (
                <Fragment>
                  <Typography variant="subtitle2">Kommentar</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Informationen zur Meldung"
                    value={state.messages[0].message || ""}
                    onChange={(e) => {
                      const message = e.target.value;
                      setState((s) =>
                        !s || !s.messages[0]
                          ? s
                          : {
                              ...s,
                              messages: [{ ...s.messages[0], message }],
                            },
                      );
                    }}
                  />
                </Fragment>
              )}

              {!state.messages[0] ? null : (
                <Fragment>
                  <Typography variant="subtitle2">Anhänge</Typography>

                  {state.messages[0].attachments.length > 0 ? (
                    <Stack spacing={1}>
                      {state.messages[0].attachments.map((fn: any, i: number) => (
                        <Paper key={String(fn.file ?? fn)} variant="outlined" sx={{ p: 1.25, display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                              {fn.originalFilename ?? fn.fileName ?? fn.file ?? String(fn)}
                            </Typography>
                          </Box>

                          <Tooltip title="Ansehen">
                            <IconButton size="small" onClick={() => window.open(imageUrl(fn.file), "_blank")}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Entfernen">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setState((s) => {
                                  if (!s || !s.messages[0]) return s;
                                  const next = [...s.messages[0].attachments];
                                  next.splice(i, 1);
                                  return {
                                    ...s,
                                    messages: [{ ...s.messages[0], attachments: next }],
                                  };
                                });
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Paper>
                      ))}
                    </Stack>
                  ) : null}

                  <ImageUploadPicker
                    apiService={ApiService}
                    onUploadMultiple={(items: any[]) => {
                      setState((s) =>
                        !s || !s.messages[0]
                          ? s
                          : {
                              ...s,
                              messages: [
                                {
                                  ...s.messages[0],
                                  attachments: s.messages[0].attachments.concat(items),
                                },
                              ],
                            },
                      );
                    }}
                    allowFiles
                    allowMultiple
                  />
                </Fragment>
              )}
            </Fragment>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        {mutation.isPending ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Speichern...
            </Typography>
          </Stack>
        ) : (
          <Button
            variant="contained"
            disabled={!valid}
            onClick={() =>
              !state ||
              mutation.mutate({
                event: fix({ ...state }, modalVisibility),
                createDamageReport: typeof state.messages[0].message !== "string" && state.messages[0].message.some((f) => f.failed),
              })
            }
          >
            Speichern
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default EventEditModal;

/**
 * =========================
 * Checklist block item (testing_json)
 * =========================
 */

type BlockProps = {
  block: EditedTestData;
  index: number;
  state: BobEventSetter | null;
  setState: React.Dispatch<React.SetStateAction<BobEventSetter | null>>;
};

export function ChecklistBlockItem({ block, index, state, setState }: BlockProps) {
  const handleStatusChange = (status: "OK" | "FAILED" | "none") => {
    setState((s) =>
      !s || typeof s.messages[0].message === "string"
        ? s
        : {
            ...s,
            messages: [
              {
                ...s.messages[0],
                message: s.messages[0].message.map((bbb, iii) =>
                  iii === index
                    ? {
                        ...bbb,
                        failed: status === "FAILED",
                        comment: status === "FAILED" ? bbb.comment : "",
                      }
                    : bbb,
                ),
              },
            ],
          },
    );
  };

  const handleCommentChange = (comment: string) => {
    setState((s) =>
      !s || typeof s.messages[0].message === "string"
        ? s
        : {
            ...s,
            messages: [
              {
                ...s.messages[0],
                message: s.messages[0].message.map((bbb, iii) =>
                  iii === index
                    ? {
                        ...bbb,
                        comment,
                      }
                    : bbb,
                ),
              },
            ],
          },
    );
  };

  const value = block.failed === undefined ? "none" : block.failed ? "FAILED" : "OK";
  const [opened, setOpen] = useState(false);

  return (
    <Fragment>
      <Stack direction="row" spacing={1} alignItems="flex-end" justifyContent="space-between">
        <Typography variant="subtitle1">{block.title}</Typography>
        {block.contents.length > 0 ? (
          <Tooltip title="Hinweise anzeigen">
            <IconButton size="small" onClick={() => setOpen((s) => !s)}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>

      {opened ? (
        <Stack spacing={0.5} sx={{ pl: 0.5 }}>
          {block.contents.map((task, t) => (
            <Stack key={t} direction="row" spacing={1} alignItems="center">
              <ArrowRightAltIcon fontSize="small" />
              <Typography variant="body2">{task}</Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}

      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, v: "OK" | "FAILED" | "none" | null) => {
          if (!v) return;
          handleStatusChange(v);
        }}
        size="small"
        sx={{ mt: 1, mb: 2 }}
      >
        <ToggleButton
          value="OK"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: value === "OK" ? "green" : undefined,
          }}
        >
          <CheckIcon fontSize="small" />
          Geprüft
        </ToggleButton>
        <ToggleButton
          value="FAILED"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: value === "FAILED" ? "red" : undefined,
          }}
        >
          <CancelIcon fontSize="small" />
          Mangel
        </ToggleButton>
      </ToggleButtonGroup>

      {block.failed ? (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          placeholder="Informationen zum Mangel"
          value={block.comment || ""}
          onChange={(e) => handleCommentChange(e.target.value)}
          sx={{ mb: 2 }}
        />
      ) : null}
    </Fragment>
  );
}
